import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MagnifyingGlass } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const UserScopeDialog = ({ open, onOpenChange, user }) => {
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [allowedZones, setAllowedZones] = useState([]);
  const [allowedSites, setAllowedSites] = useState([]);
  const [allowedAgentIds, setAllowedAgentIds] = useState([]);
  const [maxAgents, setMaxAgents] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      const [zRes, cRes, aRes, asRes, sRes] = await Promise.all([
        fetch(`${API}/api/zones`, { credentials: 'include' }),
        fetch(`${API}/api/clients`, { credentials: 'include' }),
        fetch(`${API}/api/agents`, { credentials: 'include' }),
        fetch(`${API}/api/assignments`, { credentials: 'include' }),
        fetch(`${API}/api/admin/users/${user.user_id}/scope`, { credentials: 'include' }),
      ]);
      if (zRes.ok) setZones(await zRes.json());
      if (cRes.ok) setClients(await cRes.json());
      if (aRes.ok) setAgents(await aRes.json());
      if (asRes.ok) setAssignments(await asRes.json());
      if (sRes.ok) {
        const s = await sRes.json();
        setAllowedZones(s.allowed_zones || []);
        setAllowedSites(s.allowed_sites || []);
        setAllowedAgentIds(s.allowed_agent_ids || []);
        setMaxAgents(s.max_agents ? String(s.max_agents) : '');
      }
      setAgentSearch('');
    })();
  }, [open, user]);

  // Zone principale + zones supplémentaires visibles = total accessible
  const accessibleZoneIds = useMemo(() => {
    const set = new Set(allowedZones);
    if (user?.zone_id) set.add(user.zone_id);
    return set;
  }, [allowedZones, user]);

  // Sites disponibles = union des sites des clients dans les zones accessibles
  const availableSites = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => {
      if (accessibleZoneIds.has(c.zone_id)) {
        (c.sites || []).forEach((s) => set.add(s));
      }
    });
    return Array.from(set).sort();
  }, [clients, accessibleZoneIds]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.toLowerCase().trim();
    const inZone = agents.filter((a) => accessibleZoneIds.has(a.zone_id));
    if (!q) return inZone;
    return inZone.filter((a) => a.name?.toLowerCase().includes(q) || a.agent_code?.toLowerCase().includes(q));
  }, [agents, accessibleZoneIds, agentSearch]);

  const toggleFrom = (arr, setArr, value) => {
    setArr(arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  };

  // Cocher un site : ajoute automatiquement les agents actuellement affectés à ce site.
  // Décocher un site : ne retire PAS les agents (l'admin garde le contrôle final).
  const toggleSite = (siteName) => {
    if (allowedSites.includes(siteName)) {
      setAllowedSites(allowedSites.filter((s) => s !== siteName));
      return;
    }
    const nextSites = [...allowedSites, siteName];
    const assignedAgentIds = assignments
      .filter((a) => a.site_name === siteName && a.status !== 'ended')
      .map((a) => a.agent_id)
      .filter(Boolean);
    const merged = Array.from(new Set([...allowedAgentIds, ...assignedAgentIds]));
    setAllowedSites(nextSites);
    if (assignedAgentIds.length > 0 && merged.length !== allowedAgentIds.length) {
      setAllowedAgentIds(merged);
      toast.success(`${assignedAgentIds.length} agent(s) du site « ${siteName} » ajouté(s)`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/users/${user.user_id}/scope`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allowed_zones: allowedZones,
          allowed_sites: allowedSites,
          allowed_agent_ids: allowedAgentIds,
          max_agents: maxAgents ? parseInt(maxAgents, 10) : null,
        }),
      });
      if (r.ok) { toast.success('Périmètre enregistré'); onOpenChange(false); }
      else toast.error('Erreur d\'enregistrement');
    } catch { toast.error('Erreur réseau'); }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Périmètre d&apos;accès — {user.name}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Zone principale : <span className="text-[#F8FAFC] font-medium">{user.zone_id || '—'}</span>.
            Ajoutez ci-dessous les zones/sites/agents accessibles en plus. Les listes vides = pas de restriction supplémentaire.
          </DialogDescription>
        </DialogHeader>

        {/* Zones supplémentaires */}
        <section className="rounded-md border border-[#1E293B] bg-[#1E293B]/30 p-3">
          <h4 className="text-sm font-semibold text-[#F8FAFC] mb-2">Zones supplémentaires accessibles</h4>
          <p className="text-xs text-[#94A3B8] mb-2">La zone principale « {user.zone_id || '—'} » est toujours accessible. Cochez ici les zones additionnelles.</p>
          <div className="flex flex-wrap gap-2">
            {zones.filter((z) => z.zone_id !== user.zone_id).map((z) => (
              <label key={z.zone_id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm ${allowedZones.includes(z.zone_id) ? 'bg-[#2563EB]/20 border-[#2563EB]/40 text-[#F8FAFC]' : 'bg-[#0F172A] border-[#334155] text-[#94A3B8]'}`}>
                <input type="checkbox" checked={allowedZones.includes(z.zone_id)} onChange={() => toggleFrom(allowedZones, setAllowedZones, z.zone_id)} className="accent-[#2563EB]" data-testid={`scope-zone-${z.zone_id}`} />
                {z.name}
              </label>
            ))}
            {zones.length <= 1 && <p className="text-xs text-[#64748B]">Aucune zone supplémentaire disponible.</p>}
          </div>
        </section>

        {/* Sites */}
        <section className="rounded-md border border-[#1E293B] bg-[#1E293B]/30 p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-[#F8FAFC]">Sites autorisés</h4>
            <Badge className="bg-[#334155]/40 text-[#94A3B8] border-[#334155] text-xs">{allowedSites.length} / {availableSites.length}</Badge>
          </div>
          <p className="text-xs text-[#94A3B8] mb-2">Vide = tous les sites des zones accessibles. Cocher un site sélectionne automatiquement les agents qui y sont affectés.</p>
          {availableSites.length === 0 ? (
            <p className="text-xs text-[#64748B]">Aucun site disponible dans les zones accessibles.</p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {availableSites.map((s) => (
                <label key={s} className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md border cursor-pointer text-xs ${allowedSites.includes(s) ? 'bg-[#10B981]/20 border-[#10B981]/40 text-[#F8FAFC]' : 'bg-[#0F172A] border-[#334155] text-[#94A3B8]'}`}>
                  <input type="checkbox" checked={allowedSites.includes(s)} onChange={() => toggleSite(s)} className="accent-[#10B981]" data-testid={`scope-site-${s}`} />
                  {s}
                </label>
              ))}
            </div>
          )}
        </section>

        {/* Agents whitelist + max */}
        <section className="rounded-md border border-[#1E293B] bg-[#1E293B]/30 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-[#F8FAFC]">Agents autorisés</h4>
            <div className="flex items-center gap-2">
              <Label htmlFor="max-agents" className="text-xs text-[#94A3B8]">Nombre max</Label>
              <Input id="max-agents" type="number" min="0" value={maxAgents} onChange={(e) => setMaxAgents(e.target.value)} className="w-20 h-8 bg-[#0F172A] border-[#334155] text-[#F8FAFC] text-sm" placeholder="∞" data-testid="scope-max-agents" />
              <Badge className="bg-[#334155]/40 text-[#94A3B8] border-[#334155] text-xs">{allowedAgentIds.length} sélectionné(s)</Badge>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mb-2">Vide = tous les agents des zones/sites accessibles. Cocher = restriction à ces agents précis.</p>
          <div className="flex items-center gap-2 mb-2">
            <MagnifyingGlass size={16} className="text-[#94A3B8]" />
            <Input value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} placeholder="Rechercher un agent..." className="h-8 bg-[#0F172A] border-[#334155] text-[#F8FAFC] text-sm" data-testid="scope-agent-search" />
          </div>
          {filteredAgents.length === 0 ? (
            <p className="text-xs text-[#64748B]">Aucun agent trouvé.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto">
              {filteredAgents.map((a) => (
                <label key={a.agent_id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs ${allowedAgentIds.includes(a.agent_id) ? 'bg-[#F59E0B]/10 text-[#F8FAFC]' : 'text-[#94A3B8] hover:bg-[#1E293B]/50'}`}>
                  <input type="checkbox" checked={allowedAgentIds.includes(a.agent_id)} onChange={() => toggleFrom(allowedAgentIds, setAllowedAgentIds, a.agent_id)} className="accent-[#F59E0B]" data-testid={`scope-agent-${a.agent_id}`} />
                  <span className="truncate">{a.name}</span>
                  {a.agent_code && <span className="text-[10px] text-[#64748B]">· {a.agent_code}</span>}
                </label>
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="scope-save-btn">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserScopeDialog;
